/* eslint-disable camelcase */
import { useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Stack, toast } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useIndicatorProductSubscribe } from '@/api/rest/resources/indicatorProduct';
import {
  IndicatorProduct,
  IndicatorTiersEnum,
  IndicatorTiersType,
  VerifiedByEnum,
  VerifiedByType,
} from '@/api/rest/resources/types/IndicatorProduct';
import { getStylesByCardType } from '@/components/widgets/a/Card/utils/getStylesByCardType';
import { UlListWidget } from '@/components/widgets/a/List/List';
import { PillWidget } from '@/components/widgets/a/Pill/Pill';
import { Body, Header } from '@/components/widgets/a/Text/Text';
import { VerifiedIcon } from '@/components/widgets/a/Verified/Verified';
import {
  DialogBodyWidget,
  DialogCloseWidget,
  DialogContentWidget,
  DialogHeaderWidget,
  DialogLayoutWidget,
} from '@/components/widgets/o/Dialog/Dialog';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';

import { CalculationProgress } from './CalculationProgress';

type IndicatorProductDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  indicatorProduct: IndicatorProduct;
  isRecommendedView?: boolean;
  handleToggleSelectIndicator?: () => void;
  isSelected?: boolean;
};

export const IndicatorProductDetailsDialog = ({
  open,
  onClose,
  indicatorProduct,
  isRecommendedView = false,
  handleToggleSelectIndicator,
  isSelected,
}: IndicatorProductDetailsDialogProps) => {
  const {
    widget,
    tier,
    verification_information,
    count_of_applied_assets,
    purchased_at,
    id,
    show_calculation_progress,
  } = indicatorProduct;
  const { title } = widget?.widget_format?.overview || {};

  return (
    <DialogLayoutWidget open={open} onOpenChange={onClose}>
      <DialogContentWidget className='px-0 max-w-[80vw]' withXButton={false}>
        <DetailsHeader title={title} tier={tier} />
        <DialogBodyWidget className='px-10'>
          <Stack spacing={8} direction='row'>
            <ProductIndicatorDetails
              dataSources={widget?.widget_format?.data_sources}
              verificationInformation={verification_information}
              {...widget?.widget_format?.details}
            />
            <ProductIndicatorOverview
              countOfAppliedAssets={count_of_applied_assets}
              isPurchased={!isNullish(purchased_at)}
              indicatorProductId={id}
              showCalculationProgress={show_calculation_progress}
              isRecommendedView={isRecommendedView}
              isSelected={isSelected}
              handleToggleSelectIndicator={handleToggleSelectIndicator}
            />
          </Stack>
        </DialogBodyWidget>
      </DialogContentWidget>
    </DialogLayoutWidget>
  );
};

const DetailsHeader = ({ title, tier }: { title?: string; tier?: string }) => {
  const isKnownTier = tier && Object.values(IndicatorTiersEnum).includes(tier as IndicatorTiersEnum);
  const tierPill = isKnownTier
    ? {
        i18nPath: `global.widget.indicatorLibraryCard.tier.${tier}`,
        styles: getStylesByCardType(tier as unknown as IndicatorTiersType),
      }
    : {
        text: tier,
      };

  return (
    <DialogHeaderWidget className='border-b border-divider pb-4'>
      <Stack direction='row' className='px-10 justify-between' data-testid='details-header'>
        <Stack direction='row' centerMain spacing={2}>
          {title && <Header className='typography-h3 w-full' text={title} />}
          {tier && <PillWidget {...tierPill} className='py-2 px-3 mt-[-1px]' />}
        </Stack>
        <DialogCloseWidget />
      </Stack>
    </DialogHeaderWidget>
  );
};

type ProductIndicatorDetailsProps = {
  dataSources?: string[];
  verificationInformation?: IndicatorProduct['verification_information'];
} & NonNullable<NonNullable<IndicatorProduct['widget']>['widget_format']>['details'];

const ProductIndicatorDetails = ({
  verification,
  financial_modelling,
  measurement_protocol,
  definition_and_scientific_basis,
  dataSources,
  verificationInformation,
}: ProductIndicatorDetailsProps) => {
  const { verified_by } = verificationInformation || {};
  const isKnownVerifiedBy = Object.values(VerifiedByEnum).includes(verified_by as VerifiedByEnum);

  return (
    <Stack spacing={6} className='w-1/2' data-testid='product-indicator-details'>
      {definition_and_scientific_basis && (
        <DetailsSlotSectionLayout>
          <Header
            className='typography-body1Semibold'
            i18nPath='global.widget.indicatorLibraryCard.details.definitionAndScientificBasis'
          />
          <Body className='typography-body1 text-text-secondary' text={definition_and_scientific_basis} />
        </DetailsSlotSectionLayout>
      )}
      {measurement_protocol && (
        <DetailsSlotSectionLayout>
          <Header
            className='typography-body1Semibold'
            i18nPath='global.widget.indicatorLibraryCard.details.measurementProtocol'
          />
          <Body className='typography-body1 text-text-secondary' text={measurement_protocol} />
        </DetailsSlotSectionLayout>
      )}
      {dataSources && dataSources.length > 0 && (
        <DetailsSlotSectionLayout>
          <Header
            className='typography-body1Semibold'
            i18nPath='global.widget.indicatorLibraryCard.details.dataSources'
          />
          <UlListWidget
            listItems={dataSources.map((item) => ({
              text: item,
            }))}
            className='ml-0 list-none'
          />
        </DetailsSlotSectionLayout>
      )}
      {verification && (
        <DetailsSlotSectionLayout>
          <Header
            className='typography-body1Semibold'
            i18nPath='global.widget.indicatorLibraryCard.details.verification'
          />
          <Body className='typography-body1 text-text-secondary' text={verification} />
          {verified_by && (
            <Stack spacing={1} direction='row' centerMain>
              <VerifiedIcon className='text-primary-100' />
              <Header
                i18nPath={
                  isKnownVerifiedBy
                    ? `global.widget.indicatorLibraryCard.verified.by.${verified_by as VerifiedByType}`
                    : undefined
                }
                text={isKnownVerifiedBy ? undefined : verified_by}
                className='typography-body1Semibold'
              />
            </Stack>
          )}
        </DetailsSlotSectionLayout>
      )}
      {financial_modelling && (
        <DetailsSlotSectionLayout showDivider={false}>
          <Header
            className='typography-body1Semibold'
            i18nPath='global.widget.indicatorLibraryCard.details.financialModelling'
          />
          <Body className='typography-body1 text-text-secondary' text={financial_modelling} />
        </DetailsSlotSectionLayout>
      )}
    </Stack>
  );
};

type DetailsSlotSectionLayoutProps = {
  children?: ReactNode;
  showDivider?: boolean;
};

const DetailsSlotSectionLayout = ({ children, showDivider = true }: DetailsSlotSectionLayoutProps) => {
  return (
    <Stack spacing={2} data-testid='details-slot-section'>
      {children}
      {showDivider && <Divider className='mt-2' />}
    </Stack>
  );
};

type ProductIndicatorOverviewProps = {
  countOfAppliedAssets?: number;
  isPurchased: boolean;
  indicatorProductId: string;
  showCalculationProgress?: boolean;
  isRecommendedView?: boolean;
  handleToggleSelectIndicator?: () => void;
  isSelected?: boolean;
};

const ProductIndicatorOverview = ({
  countOfAppliedAssets,
  isPurchased,
  indicatorProductId,
  showCalculationProgress,
  isRecommendedView,
  handleToggleSelectIndicator,
  isSelected,
}: ProductIndicatorOverviewProps) => {
  if (isRecommendedView) {
    return (
      <RecommendedIndicatorProductView
        handleToggleSelectIndicator={handleToggleSelectIndicator}
        isSelected={isSelected}
      />
    );
  }

  if (isPurchased) {
    return (
      <PurchasedIndicatorProductView
        countOfAppliedAssets={countOfAppliedAssets}
        indicatorProductId={indicatorProductId}
        showCalculationProgress={showCalculationProgress}
      />
    );
  }

  return <NewProductIndicatorProductView indicatorProductId={indicatorProductId} />;
};

const PurchasedIndicatorProductView = ({
  countOfAppliedAssets,
  indicatorProductId,
  showCalculationProgress,
}: {
  countOfAppliedAssets?: number;
  indicatorProductId: string;
  showCalculationProgress?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={6}
      className='bg-secondary-12 text-center p-6 rounded-3xl w-1/2'
      centerCross
      centerMain
      data-testid='purchased-indicator-product-view'
    >
      <span className='typography-h4'>{t('global.widget.indicatorLibraryCard.indicatorInUse')}</span>
      <CircleCheckIcon />
      {!isNullish(countOfAppliedAssets) && (
        <span className='typography-body1Semibold text-primary-100'>
          {t('global.widget.indicatorLibraryCard.details.appliedToAssets', { assets: countOfAppliedAssets })}
        </span>
      )}
      <CalculationProgress id={indicatorProductId} show_calculation_progress={showCalculationProgress} />
    </Stack>
  );
};

const NewProductIndicatorProductView = ({ indicatorProductId }: { indicatorProductId: string }) => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const { mutate, isPending } = useIndicatorProductSubscribe();
  const queryClient = useQueryClient();

  const handleSelectAssets = () => {
    mutate(
      {
        bodyData: {
          organization_id: organizationId,
          indicator_product_ids: [indicatorProductId],
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              'plots',
              'organization',
              'indicator-products',
              { organization_id: organizationId, purchased: true },
            ],
          });
          queryClient.invalidateQueries({
            queryKey: [
              'plots',
              'organization',
              'indicator-products',
              { organization_id: organizationId, purchased: false },
            ],
          });
        },
        onError: () => {
          toast({
            title: t('shared.account.status.notVerified.retrigger.error'),
            type: 'error',
          });
        },
      },
    );
  };

  return (
    <Stack
      spacing={10}
      className='bg-neutral-black-4 text-center p-12 rounded-3xl w-1/2'
      centerCross
      centerMain
      direction='col'
      data-testid='new-product-indicator-product-view'
    >
      <Button size='large' className='py-3 px-6 w-1/2' loading={isPending} onClick={handleSelectAssets}>
        {t('global.widget.indicatorLibraryCard.details.buttons.selectAssets')}
      </Button>
      <Button variant='outline' size='large' className='py-3 px-6 w-1/2'>
        {t('global.widget.indicatorLibraryCard.details.buttons.talkToOurTeam')}
      </Button>
    </Stack>
  );
};

const RecommendedIndicatorProductView = ({
  handleToggleSelectIndicator,
  isSelected,
}: {
  handleToggleSelectIndicator?: () => void;
  isSelected?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack
      spacing={10}
      className='bg-neutral-black-4 text-center p-12 rounded-3xl w-1/2'
      centerCross
      centerMain
      direction='col'
      data-testid='new-product-indicator-product-view'
    >
      <Button
        size='large'
        className='py-3 px-6 w-1/2'
        onClick={handleToggleSelectIndicator}
        variant={isSelected ? 'outline' : 'contained'}
      >
        {t(
          isSelected
            ? 'shared.onboarding.indicatorProducts.removeIndicator'
            : 'shared.onboarding.indicatorProducts.selectIndicator',
        )}
      </Button>
    </Stack>
  );
};

const CircleCheckIcon = () => {
  return (
    <svg
      width='118'
      height='117'
      viewBox='0 0 118 117'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      data-testid='circle-check-icon'
    >
      <circle opacity='0.5' cx='59' cy='58.5' r='58.5' stroke='#278668' strokeDasharray='2 2' />
      <g filter='url(#filter0_d_742_128082)'>
        <circle cx='59' cy='58.5' r='50.3034' fill='url(#paint0_linear_742_128082)' />
      </g>
      <path
        d='M54.5431 67.216L77.9552 43.8013L81.5593 47.4027L54.5431 74.4189L38.334 58.2097L41.9355 54.6082L54.5431 67.216Z'
        fill='white'
      />
      <defs>
        <filter
          id='filter0_d_742_128082'
          x='0.697266'
          y='0.196777'
          width='116.607'
          height='116.607'
          filterUnits='userSpaceOnUse'
          colorInterpolationFilters='sRGB'
        >
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
            result='hardAlpha'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='4' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0.298039 0 0 0 0 0.239216 0 0 0 0.2 0' />
          <feBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow_742_128082' />
          <feBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow_742_128082' result='shape' />
        </filter>
        <linearGradient
          id='paint0_linear_742_128082'
          x1='59'
          y1='8.19678'
          x2='59'
          y2='108.804'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#278668' />
          <stop offset='1' stopColor='#004C3D' />
        </linearGradient>
      </defs>
    </svg>
  );
};
